import React, { useState, useEffect } from 'react';
import { Select, Button, SelectChangeEvent, MenuItem, Grid2 as Grid, useMediaQuery,
    TextField,
    FormControl,
    InputLabel,
    Paper,
    Divider,
    Skeleton,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import { createRoute } from 'api/routes';
import { getUnassignedOrders } from 'api/orders';
import { getDrivers } from 'api/drivers';
import { Driver, Order } from 'typings/models';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import OrderSelection from './orderSelection';
import { createOrder } from 'api/orders';

const RoutesCreationPage = () => {
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>(undefined);
    const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
    const [routeName, setRouteName] = useState<string>((new Date()).toISOString().split('T')[0].replace(/-/g, ''));
    const [loading, setLoading] = useState(false);
    const [loadedData, setLoadedData] = useState(false);
    const [newOrderId, setNewOrderId] = useState<string>('');
    const [newOrderClientName, setNewOrderClientName] = useState<string>('');
    const isMobile = useMediaQuery('(max-width: 600px)');

    const fetchData = async () => {
        setLoading(true);
        setSelectedOrderIds([]);
        setSelectedDriver(undefined);
        setDrivers([]);
        setOrders([]);
        setRouteName('');
        await getDrivers().then(setDrivers);
        await getUnassignedOrders().then(setOrders);
        setLoadedData(true);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreateRoute = () => {
        if (!selectedDriver || selectedOrderIds.length === 0 || !routeName) {
            alert('Please fill all fields');
            return;
        }
        createRoute({ driverId: selectedDriver.id, orderIds: selectedOrderIds, name: routeName })
            .then(() => {
                alert('Ruta creada correctamente');
                setOrders(orders.filter(order => !selectedOrderIds.includes(order.id)));
                setSelectedDriver(undefined);
                setSelectedOrderIds([]);
                setRouteName('');
            })
            .catch(err => alert('Error creating route: ' + err.message));
    };

    const handleDriverChange = (event: SelectChangeEvent<string>) => {
        const driverId = event.target.value;
        const driver = drivers.find(d => d.id === driverId);
        setRouteName((new Date()).toISOString().split('T')[0].replace(/-/g, '') + '-' + driver?.id);
        setSelectedDriver(driver);
    };

    const handleCreateOrder = () => {
        if (!newOrderId || !newOrderClientName) {
            alert('Please fill all fields');
            return;
        }
        createOrder({ order_id: newOrderId, client_name: newOrderClientName })
            .then((newOrder) => {
                alert('Orden creada correctamente');
                setNewOrderId('');
                setNewOrderClientName('');
                setOrders([...orders, newOrder]);
                setSelectedOrderIds([...selectedOrderIds, newOrderId]);
            })
            .catch(err => alert('Error creating order: ' + err.message));
    };

    const driverSelect = (
        <FormControl fullWidth>
            <InputLabel>Chofer</InputLabel>
            <Select
                onChange={handleDriverChange}
                fullWidth
                style={{ marginBottom: 20 }}
                label="Chofer"
                value={selectedDriver ? selectedDriver.id : ""}
            >
                {drivers.map(driver => (
                    <MenuItem key={driver.id} value={driver.id}>{driver.name}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const selectedOrders = selectedOrderIds.length > 0 ? (
        <>
            <Typography variant="h6">
                {selectedOrderIds.length} {selectedOrderIds.length === 1 ? "órden" : "órdenes"} {selectedOrderIds.length === 1 ? "seleccionada" : "seleccionadas"}
            </Typography>
            <Divider />
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                <List dense>
                    {selectedOrderIds.map(orderId => (
                        <ListItem key={orderId}>
                            <ListItemText primary={orderId + " - " + orders.find(order => order.id === orderId)?.client.name} />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </>
    ) : null;

    const routeNameInput = (
        <Typography variant="h6" sx={{backgroundColor: '#E8F5E9', padding: 1}}>
            {routeName}
        </Typography>
    );

    const orderSelect = loading ? (
        <>
            <Grid size={{xs: 12, sm: 12}}>
                <Typography variant="h6">Cargando órdenes...</Typography>
                <Skeleton variant="rectangular" width="100%" height={40} />
                <br/>
                <Skeleton variant="rectangular" width="100%" height={40} />
                <br/>
                <Skeleton variant="rectangular" width="100%" height={40} />
            </Grid>
        </>
    ) : (
        <OrderSelection orders={orders} selectedOrderIds={selectedOrderIds} setSelectedOrderIds={setSelectedOrderIds} />
    );

    const createRouteButton = (
        <Button
            variant="contained"
            color="success"
            disabled={loading || !selectedDriver || selectedOrderIds.length === 0 || !routeName}
            startIcon={<AddIcon />}
            fullWidth={isMobile}
            onClick={handleCreateRoute}
        >Crear ruta</Button>
    );

    const createOrderForm = (
        <>
            <Typography sx={{marginBottom: 2}} variant="h6">Asignar factura manualmente</Typography>
            <Divider />
            <FormControl fullWidth sx={{marginBottom: 2, marginTop: 2}}>
                <TextField
                    variant="outlined"
                    label="Número de factura"
                    value={newOrderId} onChange={(e) => setNewOrderId(e.target.value)} />
            </FormControl>
            <br/>
            <FormControl fullWidth sx={{marginBottom: 2}}>
                <TextField
                    variant="outlined"
                    label="Nombre del cliente"
                    value={newOrderClientName} onChange={(e) => setNewOrderClientName(e.target.value)} />
            </FormControl>
            <Button variant="contained" color="success" onClick={handleCreateOrder}>Crear órden</Button>
        </>
    );

    const getDataButton = (
        <>
            {loadedData ?
                <Button
                    variant="contained"
                    color="secondary"
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={<RefreshIcon />}
                    onClick={fetchData}>Refrescar datos</Button> :
                <Button
                    variant="contained"
                    color="secondary"
                    disabled={loading}
                    fullWidth={isMobile}
                    startIcon={<DownloadIcon />}
                    onClick={fetchData}>Cargar datos</Button>}
        </>
    );

    const mobilePage = (
        <>
            <Grid padding={2}>
                {getDataButton}
            </Grid>
            <Grid container spacing={2} padding={2}>
                <Grid size={{xs: 12, sm: 6}}>{routeNameInput}</Grid>
                <Grid size={{xs: 12, sm: 6}}>{driverSelect}</Grid>
                <Grid size={{xs: 12, sm: 6}}>{selectedOrders}</Grid>
                <Grid size={{xs: 12, sm: 6}}>{createRouteButton}</Grid>
                <Grid size={{xs: 12, sm: 6}}>{orderSelect}</Grid>
            </Grid>
        </>
    );

    const desktopPage = (
        <>
            <Grid container spacing={2} padding={2}>
                <Grid size={{sm: 12}}>{getDataButton}</Grid>
                <Grid size={{xs: 12, sm: 6}}>
                    <Paper elevation={3}>
                        <Grid size={{xs: 12, sm: 12}}>
                            <Box sx={{padding: 2}}>
                                <Typography variant="h5">Crear nueva ruta</Typography>
                            </Box>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2} padding={2}>
                            <Grid size={{xs: 12, sm: 12}}>{routeNameInput}</Grid>
                            <Grid size={{xs: 12, sm: 12}}>{driverSelect}</Grid>
                            <Grid size={{xs: 12, sm: 12}}>{selectedOrders}</Grid>
                            <Grid size={{xs: 12, sm: 12}}>{createRouteButton}</Grid>
                        </Grid>
                    </Paper>
                    <br/>
                    {false && <Paper elevation={3}>
                        <Grid size={{xs: 12, sm: 12}} padding={2}>
                            {createOrderForm}
                        </Grid>
                    </Paper>}
                </Grid>
                <Grid size={{xs: 12, sm: 6}}>
                    <Grid container spacing={2} padding={2}>
                        {orderSelect}
                    </Grid>
                </Grid>
            </Grid>
        </>
    );

    return (
        <>
            {isMobile ? mobilePage : desktopPage}
        </>
    );
};

export default RoutesCreationPage;
